'use client'

import {
  ChangeEvent,
  useId,
  useRef,
  useState,
} from 'react'
import Image from 'next/image'
import { TextareaProps } from './types'

export function Textarea({
  id,
  label,
  value,
  defaultValue = '',
  className = '',
  required,
  disabled,
  onChange,
  onClear,
  onValueChange,
  ...props
}: TextareaProps) {
  const generatedId = useId()
  const fieldId = id ?? `textarea-${generatedId}`
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [internalValue, setInternalValue] = useState(defaultValue)
  const currentValue = value ?? internalValue

  const hasValue = currentValue.length > 0

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const nextValue = event.currentTarget.value

    if (value === undefined) {
      setInternalValue(nextValue)
    }

    onValueChange?.(nextValue)
    onChange?.(event)
  }

  function handleClear() {
    const textarea = textareaRef.current

    if (value === undefined) {
      setInternalValue('')
    }

    onValueChange?.('')
    onClear?.()

    if (textarea && onChange) {
      textarea.value = ''
      onChange({
        currentTarget: textarea,
        target: textarea,
      } as ChangeEvent<HTMLTextAreaElement>)
    }

    textarea?.focus()
  }

  return (
    <div className={`w-full min-w-0 text-left ${className}`}>
      <div
        className={`relative h-[145px] w-full rounded-xl border bg-white-50 text-white-950 transition-shadow focus-within:border-[#F1BD03] focus-within:ring-2 focus-within:ring-lightning-yellow-600 focus-within:ring-offset-2 ${
          hasValue
            ? 'border-[#F1BD03] shadow-[0_0_8px_#E8FF737A]'
            : 'border-transparent'
        } ${disabled ? 'opacity-70' : ''}`}
      >
        <label
          htmlFor={fieldId}
          className={`pointer-events-none absolute left-5 right-14 truncate text-left font-normal transition-all ${
            hasValue
              ? 'top-2 text-xs leading-3'
              : 'top-3 text-base leading-5'
          }`}
        >
          {label}
          {required ? (
            <span aria-hidden="true" className="text-[#CA0E0E]">
              {' '}
              *
            </span>
          ) : null}
        </label>
        <textarea
          ref={textareaRef}
          id={fieldId}
          value={currentValue}
          required={required}
          aria-required={required || undefined}
          disabled={disabled}
          className={`h-full w-full resize-none rounded-xl bg-transparent px-5 pr-14 font-sans text-sm leading-5 text-white-950 outline-none placeholder:text-transparent ${
            hasValue ? 'pb-3 pt-7' : 'py-3'
          }`}
          onChange={handleChange}
          {...props}
        />
        {hasValue && !disabled ? (
          <button
            type="button"
            className="absolute right-1 top-1 flex size-11 cursor-pointer items-center justify-center rounded-full text-white-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2"
            aria-label={`Limpar ${label}`}
            onClick={handleClear}
          >
            <span className="flex size-6 items-center justify-center rounded-full bg-lightning-yellow-500">
              <Image
                src="/assets/icons/x.svg"
                alt=""
                width={16}
                height={16}
                aria-hidden="true"
                className="size-4"
              />
            </span>
          </button>
        ) : null}
      </div>
    </div>
  )
}
